import { GetStaticPropsContext } from 'next';
import { NextSeoProps } from 'next-seo';

import { PageCategory } from '~/features/analytics/constants';
import { getCompany } from '~/features/companies/controllers/get-company';
import { publicEnv } from '~/features/environment/public';
import {
  StaticDownloadGuideAsideProps,
  transformStaticDownloadGuideAsideProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/download-guide-aside';
import {
  StaticDownloadSyllabusButtonProps,
  transformStaticDownloadSyllabusButtonProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/download-syllabus-button';
import { PageHead } from '~/features/experiments/ab_plp_redesign_2025/components/page-head';
import {
  StaticPriceCardProps,
  transformStaticPriceCardProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/price-card';
import {
  StaticProgramAboutSectionProps,
  transformStaticProgramAboutSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-about-section';
import {
  StaticProgramBreadcrumbsProps,
  transformStaticProgramBreadcrumbsProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-bread-crumbs';
import {
  StaticProgramDetailsSectionProps,
  transformStaticProgramDetailsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-details-section';
import {
  StaticProgramInstructorsSectionProps,
  transformStaticProgramInstructorsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-instructors-section';
import { ProgramLandingPageChallenger } from '~/features/experiments/ab_plp_redesign_2025/components/program-landing-page';
import {
  StaticProgramOutlineSectionProps,
  transformStaticProgramOutlineSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-outline-section';
import {
  StaticRelatedProgramsSectionProps,
  transformStaticRelatedProgramsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/related-programs-section';
import {
  StaticReviewsSectionProps,
  transformStaticReviewsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/reviews-section';
import {
  StaticSalariesSectionProps,
  transformStaticSalariesSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/salaries-section';
import {
  StaticProgramUpsellAsideProps,
  transformStaticProgramUpsellAsideProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/upsell-program-aside';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { logger } from '~/features/logging/logger';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import type { Page } from '~/features/page/models/page';
import { transformSeoToNextSeo } from '~/features/page/utils/transform-seo-to-next-seo';
import { getProgram } from '~/features/program/controllers/get-program';
import { getProgramContent } from '~/features/program/controllers/get-program-content';
import { getProgramMetadata } from '~/features/program/controllers/get-program-metadata';
import { getProgramReviewSummary } from '~/features/program/controllers/get-program-review-summary';
import { getProgramReviews } from '~/features/program/controllers/get-program-reviews';
import { getProgramVersion } from '~/features/program/controllers/get-program-version';
import { useEnrollmentRedirect } from '~/features/program/hooks/use-enrollment-redirect';
import { usePLPViewedEvent } from '~/features/program/hooks/use-plp-viewed-event';
import { isNanodegreeProgram } from '~/features/program/utils/is-program-type';
import { createProgramStructuredData } from '~/features/seo/utils/create-plp-structured-data';
import { useSessionReplay } from '~/features/session-replay';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

export default function ProgramPage({
  page,
  programKey,
  imageUrl,
  structuredData,
  companySlug,
  staticBreadcrumbsProps,
  staticProgramDetailsSectionProps,
  staticProgramOutlineSectionProps,
  staticProgramInstructorsSectionProps,
  staticSalariesSectionProps,
  staticReviewsSectionProps,
  staticProgramAboutSectionProps,
  staticDownloadGuideAsideProps,
  staticDownloadSyllabusButtonProps,
  staticRelatedProgramsSectionProps,
  staticUpsellProgramAsideProps,
  isProgramFree,
  difficultyLevel,
  staticPriceCardProps,
  seoProps,
  semanticType,
  isMastersBannerRendered,
  isMbaBannerRendered,
}: {
  page: Page;
  programKey: string;
  imageUrl: string | null;
  structuredData: Record<string, any>;
  companySlug?: string;
  staticBreadcrumbsProps: StaticProgramBreadcrumbsProps;
  staticProgramDetailsSectionProps: StaticProgramDetailsSectionProps;
  staticProgramOutlineSectionProps: StaticProgramOutlineSectionProps;
  staticProgramInstructorsSectionProps: StaticProgramInstructorsSectionProps;
  staticPriceCardProps: StaticPriceCardProps;
  staticSalariesSectionProps: StaticSalariesSectionProps;
  staticReviewsSectionProps: StaticReviewsSectionProps;
  staticProgramAboutSectionProps: StaticProgramAboutSectionProps;
  staticDownloadGuideAsideProps: StaticDownloadGuideAsideProps;
  staticDownloadSyllabusButtonProps: StaticDownloadSyllabusButtonProps;
  staticRelatedProgramsSectionProps: StaticRelatedProgramsSectionProps;
  staticUpsellProgramAsideProps: StaticProgramUpsellAsideProps;
  isMastersBannerRendered: boolean;
  isMbaBannerRendered: boolean;
  isProgramFree?: boolean;
  difficultyLevel?: string;
  semanticType?: string;
  seoProps: NextSeoProps;
}) {
  usePLPViewedEvent({
    isFree: isProgramFree,
    nodeKey: programKey,
  });

  useSessionReplay();

  useEnrollmentRedirect(programKey);

  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: isProgramFree ? PageCategory.FreeProgram : PageCategory.PaidProgram,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  return (
    <>
      <PageHead
        page={page}
        pageCategory={isProgramFree ? PageCategory.FreeProgram : PageCategory.PaidProgram}
        seoProps={seoProps}
        additionalPageAnalytics={{
          difficulty: difficultyLevel,
          semanticType,
          degree_key: programKey,
        }}
      />
      <PageLayout withoutVerticalPadding>
        <ProgramLandingPageChallenger
          companySlug={companySlug}
          imageUrl={imageUrl}
          isMastersBannerRendered={isMastersBannerRendered}
          isMbaBannerRendered={isMbaBannerRendered}
          isProgramFree={isProgramFree}
          programKey={programKey}
          staticBreadcrumbsProps={staticBreadcrumbsProps}
          staticDownloadGuideAsideProps={staticDownloadGuideAsideProps}
          staticDownloadSyllabusButtonProps={staticDownloadSyllabusButtonProps}
          staticPriceCardProps={staticPriceCardProps}
          staticProgramAboutSectionProps={staticProgramAboutSectionProps}
          staticProgramDetailsSectionProps={staticProgramDetailsSectionProps}
          staticProgramInstructorsSectionProps={staticProgramInstructorsSectionProps}
          staticProgramOutlineSectionProps={staticProgramOutlineSectionProps}
          staticRelatedProgramsSectionProps={staticRelatedProgramsSectionProps}
          staticReviewsSectionProps={staticReviewsSectionProps}
          staticSalariesSectionProps={staticSalariesSectionProps}
          staticUpsellProgramAsideProps={staticUpsellProgramAsideProps}
        />
        <script dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} type='application/ld+json' />
      </PageLayout>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  locale,
}: GetStaticPropsContext<{
  slug: string;
  companySlug?: string;
}>) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const programSlug = params?.slug;
  const programSlugPrefix = programSlug?.split('--')[0];
  const programKey = programSlug?.split('--')[1];

  if (!programKey) {
    logger.fatal('Program key not found in provided slug', {
      file: 'src/pages/course/[slug].tsx',
      function: 'getStaticProps',
      programSlug,
    });

    return {
      notFound: true,
    };
  }

  // Unpublished programs will return null
  const latestProgramVersion = await getProgramVersion(programKey);

  // Don't allow access to unpublished programs in production
  if (!latestProgramVersion && publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production') {
    return {
      notFound: true,
    };
  }

  try {
    const program = await getProgram({ key: programKey, version: latestProgramVersion });

    const programPartKeys = isNanodegreeProgram(program) ? program.parts.map((part) => part.key) : [];

    const [page, programMetadata, programContent, reviewSummary, reviews] = await Promise.all([
      getPage('/program'),
      getProgramMetadata({ programKeys: [program.key, ...programPartKeys] }),
      getProgramContent(programKey),
      getProgramReviewSummary(programKey),
      getProgramReviews(programKey, 1, 5),
    ]);

    /**
     * Note: we have some enterprise pages that do not have access to the program slug and instead uses "--<programKey>" as a shortcut slug to get to the PLP.
     * To prevent these duplicate urls from being indexed, we need to set the noindex and nofollow meta tags using the Program entry slug as the source of truth.
     */
    if (programSlug !== programContent?.slug && page?.seo) {
      page.seo.noindex = true;
      page.seo.nofollow = true;
    }

    /**
     * Here we are redirecting pages using the wrong slug ("abc123--<programKey>"), but still allowing slugs like "--<programKey>"
     */

    if (programSlug !== programContent?.slug && programSlugPrefix !== '') {
      return programContent?.slug
        ? {
            redirect: {
              destination: `${programContent.slug}`,
              permanent: true,
            },
          }
        : {
            notFound: true,
          };
    }

    let structuredData = createProgramStructuredData({
      program,
      programContent,
      programMetadata,
      reviews,
      reviewSummary,
    });

    if (publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'production') {
      // Note: on preview environments, we need to remove the `undefined` values for props to be serialized, properly.
      // It is often the case that values are missing while stakeholders are entering content into Sanity.
      structuredData = JSON.parse(JSON.stringify(structuredData)) as Record<string, any>;
    }

    const partnerName = programMetadata.rootProgram?.partners?.[0]?.name;
    const partner = partnerName ? await getCompany(partnerName) : null;

    const staticBreadcrumbsProps = transformStaticProgramBreadcrumbsProps({ programMetadata, program });
    const staticProgramDetailsSectionProps = transformStaticProgramDetailsSectionProps({
      program,
      programMetadata,
      programContent,
      reviewSummary,
      partner,
    });
    const staticProgramOutlineSectionProps = transformStaticProgramOutlineSectionProps({ program });
    const staticProgramInstructorsSectionProps = transformStaticProgramInstructorsSectionProps({ programContent });
    const staticPriceCardProps = transformStaticPriceCardProps({ program });
    const staticSalariesSectionProps = transformStaticSalariesSectionProps({ programContent });
    const staticReviewsSectionProps = transformStaticReviewsSectionProps({ programKey, reviews, reviewSummary });
    const staticProgramAboutSectionProps = transformStaticProgramAboutSectionProps({ programContent });
    const staticDownloadGuideAsideProps = transformStaticDownloadGuideAsideProps({ programContent, program });
    const staticDownloadSyllabusButtonProps = transformStaticDownloadSyllabusButtonProps({ programContent, program });
    const staticRelatedProgramsSectionProps = transformStaticRelatedProgramsSectionProps({ programKey });
    const staticUpsellProgramAsideProps = transformStaticProgramUpsellAsideProps({ programContent });

    const [coreModuleKeys, electiveKeys, mbaBannerKeys] = await Promise.all([
      import('~/features/accreditation/constants/approved-core-module-keys.json').then((module) => module.default),
      import('~/features/accreditation/constants/approved-elective-keys.json').then((module) => module.default),
      import('~/features/accreditation/constants/mba-banner-keys.json').then((module) => module.default),
    ]);
    const isMbaBannerRendered = mbaBannerKeys.includes(programKey);
    const isMastersBannerRendered =
      !isMbaBannerRendered && (coreModuleKeys.includes(programKey) || electiveKeys.includes(programKey));

    return {
      props: {
        page,
        programKey,
        semanticType: program?.semanticType ?? null,
        imageUrl: programMetadata.rootProgram?.catalogImageUrl ?? null,
        difficultyLevel: programMetadata.rootProgram?.difficultyLevel?.name || null,
        isProgramFree: programContent?.isFree ?? null,
        structuredData,
        companySlug: params?.companySlug ?? null,
        staticBreadcrumbsProps,
        staticProgramDetailsSectionProps,
        staticProgramOutlineSectionProps,
        staticProgramInstructorsSectionProps,
        staticPriceCardProps,
        staticSalariesSectionProps,
        staticReviewsSectionProps,
        staticProgramAboutSectionProps,
        staticDownloadGuideAsideProps,
        staticDownloadSyllabusButtonProps,
        staticRelatedProgramsSectionProps,
        staticUpsellProgramAsideProps,
        seoProps: JSON.parse(JSON.stringify(transformSeoToNextSeo(page.seo, programContent, programMetadata))),
        isMastersBannerRendered,
        isMbaBannerRendered,
      },
      revalidate: 60 * 60 * 24, // TODO: remove once we figure out issues with CI hanging.
    };
  } catch (error) {
    logger.error('An error occurred building Program page', {
      file: 'src/pages/course/[slug].tsx',
      function: 'getStaticProps',
      programSlug,
      error,
    });

    return {
      notFound: true,
    };
  }
}
